import React from 'react';
import {
  Formik,
  Field,
} from 'formik';
import i18n from 'i18next';
import * as Yup from 'yup';

import {
  slugify,
} from 'web-utils';

import {
  Button,
  TextField,
  SelectField,
} from 'web-components';

import {
  Flex,
  Text,
  H200,
} from 'web-styled';

import * as S from '../styled';

const types = [
  {
    label: 'One Time',
    value: 'onetime',
  },
  {
    label: 'Recurring',
    value: 'recurring',
  },
];

const periods = [
  {
    label: 'Monthly',
    value: 'monthly',
  },
  {
    label: 'Annual',
    value: 'annual',
  },
];

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Name is required'),
  description: Yup.string()
    .required('Description is required'),
  amount: Yup.number()
    .required('Amount is required'),
});

export interface InfoInterface {
  name: string;
  description: string;
  amount: string;
  slug: string;
  type: string;
  period: string;
  trialperiod: string;
}

type Props = {
  initialValues: InfoInterface;
  onSubmit: { (): void };
  onCancel: { (): void };
};

const BasicInfo = ({
  initialValues,
  onSubmit,
  onCancel,
}: Props) => (
  <Formik
    validateOnBlur={false}
    validationSchema={validationSchema}
    initialValues={initialValues}
    onSubmit={onSubmit}
  >
    {(formikProps) => (
      <Flex
        column
        as="form"
        width="100%"
        spacing={15}
        onSubmit={formikProps.handleSubmit}
      >
        <H200>Basic Info</H200>
        <Field
          name="name"
          label="Product Name"
          onChange={(e: any) => {
            const name = e.target.value || '';
            formikProps.setFieldValue('name', name);
            formikProps.setFieldValue('slug', slugify(name));
          }}
          placeholder={i18n.t('product.name.placeholder')}
          component={TextField}
        />
        <Field
          name="description"
          label="Product Description"
          textarea
          minRows={3}
          placeholder={i18n.t('product.description.placeholder')}
          component={TextField}
        />
        <Flex
          width="100%"
          spacing={10}
          column
        >
          <Text color="#888" size="13px">
            URL
          </Text>
          <Flex
            width="100%"
            spacing={10}
          >
            <S.SlugPrefix>
              {`${window.location.hostname}/`}
            </S.SlugPrefix>
            <Field
              name="slug"
              placeholder="barbellstrength"
              component={TextField}
            />
          </Flex>
        </Flex>
        <H200>Pricing</H200>
        <Flex
          width="100%"
          align="flex-start"
          spacing={24}
        >
          <S.AmountWrapper>
            <span>
              $
            </span>
            <Field
              name="amount"
              label="Amount"
              type="number"
              placeholder="XX.XX"
              component={TextField}
            />
          </S.AmountWrapper>

          <Field
            name="type"
            label="Type"
            width="20%"
            options={types}
            component={SelectField}
          />

          {formikProps.values.type === 'recurring' && (
            <>
              <Field
                name="period"
                label="Period"
                width="20%"
                options={periods}
                component={SelectField}
              />
              <Field
                name="trialperiod"
                label="Trial Period"
                width="20%"
                options={Array.from({ length: 8 }).map((_, i) => ({
                  value: i.toString(),
                  label: `${i} day${i > 1 ? 's' : ''}`,
                }))}
                component={SelectField}
              />
            </>
          )}
        </Flex>

        <Flex
          width="100%"
          justify="space-between"
          margin="30px 0 0 0"
        >
          <Button
            ghost
            width="140px"
            spacing="large"
            appearance="danger"
            isDisabled={formikProps.isSubmitting}
            onClick={onCancel}
          >
            Cancel
          </Button>

          <Button
            ghost
            width="140px"
            type="submit"
            spacing="large"
            appearance="primary"
            isLoading={formikProps.isSubmitting}
            isDisabled={formikProps.isSubmitting}
          >
            Next
          </Button>
        </Flex>
      </Flex>
    )}
  </Formik>
);

export default BasicInfo;
